import inlineCTA from "assets/images/psp/inlineCTA.jpg";
import illustrationSearch from "assets/images/psp/illustration-search.svg";
import GeorgeRansom from "assets/images/psp/review-george.png";
import AnnAlarcon from "assets/images/psp/review-ann.png";
import FemaleAvatar from "assets/images/psp/femaleavatar.jpg"
import BusinessImage1 from "assets/images/psp/business-1.webp";
import BusinessImage2 from "assets/images/psp/business-2.webp";
import typesofBusinessStorage from "assets/images/psp/6-types-business.webp";
import businessesThatBenefit from "assets/images/psp/10-businesses.webp";
import smallBusinessStorage from "assets/images/psp/small-business-storage.webp";
import ecommerceStorage from "assets/images/psp/ecommerce.webp";
import HeaderBackgroundDesktop from "assets/images/psp/hero/business-hero.webp";
import HeaderBackgroundMobile from "assets/images/psp/hero/business-header-mobile.webp";
import HeaderBackgroundTablet from "assets/images/psp/hero/business-header-tablet.webp";
import { storageCategories } from "../utils/getPSPLinks";

const industrialStorageRichContent = `
When your business needs to move beyond basic storage, industrial storage units provide warehouse-style space without the commitment. These larger facilities accommodate everything from commercial vehicle storage for delivery fleets to extensive office storage and warehousing for growing operations.

**Key advantages over traditional warehouse leases:**

* Month-to-month flexibility vs. multi-year commitments
* No build-out costs or utility deposits
* Immediate availability vs. lengthy approval processes
* Scale up or down as business needs change
`;

const faqs = [
	{
		question:
			"How do I find the right size commercial storage unit for my business?",
		answer: "Start by inventorying everything you plan to store and measuring your largest items. Small business storage units like 10x10 spaces work well for most service-based businesses, while product-based companies often need 10x20 or larger units. If you're expanding, choose a size that accommodates 6-12 months of projected growth."
	},
	{
		question: "Can business storage costs be tax-deductible?",
		answer: "In most cases, yes. Storage rentals for legitimate business purposes typically qualify as a deductible business expense under IRS guidelines. This includes storing inventory, equipment, documents, and supplies necessary for business operations."
	},
	{
		question:
			"What's the difference between commercial storage and regular self-storage units?",
		answer: "Commercial storage facilities are specifically designed for business use and offer features that standard self-storage typically doesn't provide. These include extended or 24/7 access hours, loading dock access, larger unit sizes, business services like package receiving, and commercial-grade security systems."
	},
	{
		question:
			"How secure are items stored in commercial storage facilities?",
		answer: "Modern commercial storage facilities employ multiple security layers to protect business assets. Most feature gated access with personalized entry codes, comprehensive surveillance camera systems, individual unit alarms, and on-site management during business hours."
	},
	{
		question: "What items cannot be stored in commercial storage units?",
		answer: "Commercial storage facilities prohibit hazardous materials, flammable substances, perishable goods, live animals, illegal items, and anything that could pose safety risks to other tenants. Specific restrictions vary by facility but commonly include gasoline, paint thinners, compressed gases, firearms (in some locations), and toxic chemicals."
	}
];

const relatedContentCards = [
	{
		category: "Storage for Business",
		url: "https://www.storage.com/blog/warehouse-vs-self-storage-cost-comparison/",
		date: "October 31, 2025",
		image: smallBusinessStorage,
		content:
			"A Guide to Small Business Storage: Finding the Right Fit for Your Budget"
	},
	{
		category: "Storage for Business",
		url: "https://www.storage.com/blog/types-of-business-storage/",
		date: "December 18, 2025",
		image: typesofBusinessStorage,
		content: "6 Types of Business Storage: Best Uses and Features"
	},
	{
		category: "Self Storage",
		url: "https://www.storage.com/blog/e-commerce-storage/",
		date: "October 31, 2025",
		image: ecommerceStorage,
		content:
			"E-commerce Storage: How To Use Self Storage To Grow Your E-commerce Business"
	},
	{
		category: "Storage for Business",
		url: "https://www.storage.com/blog/self-storage-for-business/",
		date: "December 19, 2025",
		image: businessesThatBenefit,
		content: "10 Businesses That Benefit from Using Self-Storage"
	}
];

const reviewsContent = {
	title: "Storage.com has a 4.4 out of 5 star rating on",
	testimonials: [
		{
			body: "I needed a space to park a car trailer for four days. I called Storage.com and they immediately found a space with great pricing. The reservation took less than 5 minutes, and I got a call from the storage unit manager within 10 minutes. I was very grateful and relieved to be able to make these arrangements so easily.",
			author: "George Ransom",
			title: "Storage.com is easy, quick, and responsive",
			imageSrc: GeorgeRansom
		},
		{
			body: "Storage.com did an excellent job helping me find a storage facility at a great price. Better than the price that I found on my own.",
			author: "Alison",
			title: "Storage facility search",
			imageSrc: AnnAlarcon
		},
		{
			body: "I had never used a storage unit before and had many questions regarding the necessary size, locations, climate-controlled, etc. Storage.com's rep made sure all of my questions were answered and the reservation was made.",
			author: "Hilary Martin",
			title: "Excellent help when I needed it!",
			imageSrc: FemaleAvatar
		}
	]
};

const commercialHerodescription = `
Your [business is growing](https://storage.com/blog/storage-unit-for-small-business/), but your overhead doesn't have to. Commercial storage facilities offer the space you need without complex warehouse lease commitments or expansion headaches.\n\n
Whether you're managing inventory overflow, storing equipment and vehicles between jobs, or need secure document storage, business storage units deliver flexibility and cost benefits that traditional warehousing can't match.\n\n
Storage.com connects you with verified commercial storage facilities nationwide, so you can focus on scaling and growing, instead of storing and spending.
`;

const businessContentSection = `
**[eCommerce & Online Retail](https://storage.com/blog/e-commerce-storage/)**
- *Use Cases:*
    - Excess inventory
    - Seasonal products
    - Returns processing
    - Packing & shipping supplies
    - High-value item storage
- *Benefits:*
    - Reduces clutter
    - Ensures readiness for peak sales seasons
    - Streamlines logistics
    - Organization for efficient fulfillment
    - Added security for pricey items

**Brick & Mortar Retailers**
- *Use Cases:*
    - Seasonal decorations and inventory
    - Overflow management
    - Promotional material storage
    - Documentation & record storage
- *Benefits:*
    - Frees up valuable space during the off-season
    - Enables stock rotations and display management
    - Organized marketing materials
    - Document management and secure storage

**Construction and Labor Services**
- *Use Cases:*
    - Tool and equipment storage
    - Bulky material storage
    - Commercial vehicle storage
    - Job site organization
- *Benefits:*
    - Ensures safety and security between jobs, reduces wear and tear/exposure to the elements
    - Clears up space otherwise taken up by lumber, tile, and fixtures
    - Streamlines project setup and execution
    - Facilitates safer, less cluttered work environments

**Real Estate, Property Management, Interior Design**
- *Use Cases:*
    - Staging furniture and seasonal decor
    - Document and file storage
    - Signage and banner storage
    - Sample management
    - Appliance, fixture, DIY tool storage
- *Benefits:*
    - Enables versatile property presentation
    - Safeguards client documentation and sensitive information
    - Organizes and protects pricey marketing assets and deliverables
`;

export const PSPBusinessContent = {
	type: "Business",
	sections: [
		{
			title: "Commercial Storage hero",
			noPadding: true,
			modules: [
				{
					type: "hero-section",
					props: {
						title: "Find the Top Commercial Storage Units",
						description: commercialHerodescription,
						searchPlaceholder: "Search by city, state, or zip...",
						buttonText: "Find Commercial Storage",
						heroImages: {
							desktop: HeaderBackgroundDesktop,
							tablet: HeaderBackgroundTablet,
							mobile: HeaderBackgroundMobile
						},
						heroAltText: "Commercial Storage Hero Image"
					}
				}
			]
		},
		{
			title: "Business Storage Introduction",
			noPadding: true,
			modules: [
				{
					type: "content-section",
					props: {
						title: "The Real Cost of Poor Storage Planning",
						background: "white",
						content: [
							{
								id: "1",
								value: "Every successful business owner knows this scenario: You're hitting your revenue targets, orders are flowing, but suddenly you're drowning in inventory with nowhere to put it."
							},
							{
								id: "2",
								value: "Your office looks like a warehouse, your garage is packed, and you're paying premium prices for rushed deliveries because you can't find what you need when you need it."
							},
							{
								id: "3",
								value: "To top it all off, you've got nowhere safe to store your company vehicles between events or jobs."
							},
							{
								id: "4",
								value: "Small business storage units solve this problem at a fraction of warehouse lease costs. Instead of committing to expensive long-term commercial space, you get [secure, accessible storage that scales with your needs](https://storage.com/blog/what-is-maxi-storage/)."
							}
						],
						isColumnWise: false
					}
				}
			]
		},
		{
			title: "Businesses That Benefit",
			noPadding: true,
			modules: [
				{
					type: "content-section",
					// isColumnWise - INVERTED: true = row layout, false = column layout
					props: {
						title: "Businesses That Benefit from Self Storage and Commercial Storage Units",
						background: "light-blue",
						description:
							"Commercial storage and storage units for businesses are a major asset for businesses across all industries. Here are just a few businesses that are currently leveraging storage units to scale and grow their business.",
						content: [
							{
								id: "1",
								value: businessContentSection
							},
						],
						isColumnWise: false,
						listType: "single"
					}
				}
			]
		},
		{
			title: "Storage Impact on Business",
			noPadding: true,
			modules: [
				{
					type: "video-content",
					props: {
						title: "How Do Inefficient Physical Storage Practices Affect Your Bottom Line?",
						background: "blue",
						additionalContent: true,
						markDown:
							"When you improve your business storage practices, you improve all of your internal operational systems.\n\n[McKinsey](https://www.articlecube.com/research-shows-searching-information-work-wastes-time-and-money) recently reported that business employees spend nearly 2 hours per day (almost 10 hours/week) searching for information. That's almost a quarter of the day simply looking through documentation—rather than completing tasks that can move the needle for your business.\n\n[IDC found that the time workers spend searching for information or tools](https://computhink.com/wp-content/uploads/2015/10/IDC20on20The20High20Cost20Of20Not20Finding20Information.pdf) can cost an organization up to $2.5 million per year.\n\nConsidering that document management is one of many business storage considerations, you could be losing more time and money than you think simply searching for documentation, tools, and equipment.\n\nStorage units for business are an affordable investment that buys you more than extra square footage; it buys more time in the day for you and your employees to thrive.",
						mediaType: "image",
						mediaUrl: BusinessImage1,
						videoTitle: "",
						textAlign: "left"
					}
				}
			]
		},
		{
			title: "Commercial Storage Size Comparison",
			noPadding: true,
			modules: [
				{
					type: "size-comparison",
					props: {
						title: "Commercial Storage Solutions by Business Type",
						background: "light-blue",
						description: "",
						sizes: [
							{
								id: "5x10",
								label: "5x10",
								title: "5' Wide x 10' Long",
								description:
									"* $80-$150/month*\n* **Best For:** Home-based businesses, consultants\n* **Commonly Stored:** Office supplies, seasonal inventory, client files"
							},
							{
								id: "10x10",
								label: "10x10",
								title: "10' Wide x 10' Long",
								description:
									"* $120-$200/month*\n* **Best For:** Service professionals, small retailers\n* **Commonly Stored:** Equipment, product inventory, marketing materials"
							},
							{
								id: "10x20",
								label: "10x20",
								title: "10' Wide x 20' Long",
								description:
									"* $180-$300/month*\n* **Best For:** Growing e-commerce, contractors\n* **Commonly Stored:** Commercial vehicle storage, bulk inventory, tools"
							},
							{
								id: "20x20",
								label: "20x20",
								title: "20' Wide x 20' Long",
								description:
									"* $350-$500/month*\n* **Best For:** Established businesses, wholesalers\n* **Commonly Stored:** Warehouse storage space for large inventory, multiple vehicles"
							}
						],
						postModuleDescription:
							"*\\*Pricing varies by location and amenities. Climate control, security features, and access hours affect monthly rates.*"
					}
				}
			]
		},
		{
			title: "Industrial Storage Units",
			noPadding: true,
			modules: [
				{
					type: "video-content",
					props: {
						title: "Industrial Storage Units for Serious Operations",
						background: "blue",
						description: "",
						additionalContent: true,
						markDown: industrialStorageRichContent,
						mediaType: "image",
						mediaUrl: BusinessImage2,
						videoTitle: ""
					}
				}
			]
		},
		{
			title: "Commercial vs Traditional Storage",
			noPadding: true,
			modules: [
				{
					type: "content-section",
					props: {
						title: "What Makes Commercial Storage Facilities Different From Traditional Storage Units",
						background: "light-blue",
						listType: "single",
						content: [
							{
								id: "1",
								value: "Unlike residential storage, commercial storage facilities cater specifically to business needs:"
							},
							{
								id: "2",
								value: "**Extended Access Hours:** Many locations offer 24/7 access for business customers"
							},
							{
								id: "3",
								value: "**Loading Dock Access:** [Drive-up units](https://storage.com/blog/drive-up-vs-first-floor-access-self-storage/) and dock-high spaces for efficient loading"
							},
							{
								id: "4",
								value: "**Business Services:** Package receiving, freight coordination, and mail services"
							},
							{
								id: "5",
								value: "**Enhanced Security:** Gated access, surveillance systems, and individual unit alarms"
							},
							{
								id: "6",
								value: "**Professional Contracts:** Business-friendly terms with commercial insurance options"
							}
						],
						isColumnWise: false
					}
				}
			]
		},
		{
			title: "Business Storage CTA",
			noPadding: true,
			modules: [
				{
					type: "inline-cta",
					props: {
						storageType: "Business",
						background: "white",
						subheading: "Make a free reservation in minutes!",
						searchPlaceholder: "Search by city, state, or zip...",
						searchButtonText: "Find Storage",
						image: inlineCTA,
						imageAltText: "Find commercial storage"
					}
				}
			]
		},
		{
			title: "Business Customer Reviews",
			noPadding: true,
			modules: [
				{
					type: "reviews",
					props: { ...reviewsContent, background: "blue" }
				}
			]
		},
		{
			title: "Commercial Storage FAQs",
			noPadding: true,
			modules: [
				{
					type: "faq",
					props: {
						title: "Commercial Storage FAQs",
						description: "",
						faqs
					}
				}
			]
		},
		{
			title: "Business Storage Related Content",
			noPadding: true,
			modules: [
				{
					type: "related-content",
					props: {
						heading: "Learn More About Business Storage",
						background: "light-blue",
						unitCards: relatedContentCards
					}
				}
			]
		},
		{
			title: "Business Links to other PSP",
			noPadding: true,
			modules: [
				{
					type: "psp-links",
					props: {
						background: "white",
						storageGridContent: {
							title: "More Ways to Store",
							image: illustrationSearch,
							storageTypes: storageCategories.business
						}
					}
				}
			]
		},
		{
			title: "Storage Near You",
			noPadding: true,
			modules: [
				{
					type: "nearby-cities",
					props: {
						storageType: "business",
						background: "light-blue"
					}
				}
			]
		}
	]
};

export default PSPBusinessContent;
